import { useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import {
  Paper, Grid, Typography, TextField, Button, MenuItem, Checkbox, FormControlLabel,
  CircularProgress, Table, TableHead, TableBody, TableRow, TableCell, Divider,
  Tabs, Tab, IconButton,
} from '@material-ui/core';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import AddIcon from '@material-ui/icons/Add';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { listSpecs, getSpec, saveSpec, listSqlSources, convertSqlToSpec, listTargetColumns } from '../../../Actions';
import { applyMapping, specColumns } from '../../../utils/mappingEngine';
import SlotEditor, { makeSourceRenderOption } from './SlotEditor';
import {
  RULE_KINDS, FILTER_OPS, STARTER_SPEC,
  detectRuleKind, summarizeRule, normalizeSpec, serializeSpec, collectSourceCols,
} from './ruleUtils';

const MATCHING_TYPES = [
  { value: '', label: 'Standard (no special matching)' },
  { value: 'rfms_id_match_sku_partial', label: 'RFMS — SKU, partial (upsert)' },
  { value: 'rfms_id_match_sku_full', label: 'RFMS — SKU, full (replace/remove unmatched)' },
  { value: 'rfms_id_match_product_partial', label: 'RFMS — product, partial (upsert)' },
  { value: 'rfms_id_match_product_full', label: 'RFMS — product, full (replace/remove unmatched)' },
  { value: 'pacific_solutions_id_match_sku_partial', label: 'Pacific Solutions — SKU, partial' },
];

// Every source column a spec references — used to flag typos against the sample's headers.
function referencedColumns(spec) {
  const cols = new Set();
  const addFilters = (fs) => (fs || []).forEach((f) => f?.col && cols.add(f.col));
  const addExpr = (e) => {
    if (!e || typeof e !== 'object') return;
    if (e.col) cols.add(e.col);
    ['add', 'sub', 'mul', 'coalesce'].forEach((k) => Array.isArray(e[k]) && e[k].forEach(addExpr));
  };
  const addRule = (r) => {
    if (!r || typeof r !== 'object') return;
    if (r.from) cols.add(r.from);
    if (Array.isArray(r.concat)) r.concat.forEach((c) => cols.add(c));
    if (r.expr) addExpr(r.expr);
    if (r.if) { addFilters(r.if); addRule(r.then); addRule(r.else); }
  };
  addFilters(spec.rowFilters);
  (spec.outputs || []).forEach((t) => {
    addFilters(t.when);
    Object.values(t.fields || {}).forEach(addRule);
  });
  return [...cols];
}

// Reusable row of {col, op, value} filters (used for per-output `when` and spec-level rowFilters).
function FilterBuilder({ label, help, filters, sourceHeaders, sampleFirstRow, usedSources, onChange }) {
  const list = Array.isArray(filters) ? filters : [];
  const renderOpt = makeSourceRenderOption(sampleFirstRow, usedSources);
  const set = (i, f) => onChange(list.map((x, j) => (j === i ? f : x)));
  const remove = (i) => onChange(list.filter((_, j) => j !== i));
  const add = () => onChange([...list, { col: '', op: 'nonEmptyNonZero' }]);
  return (
    <div style={{ marginTop: 8 }}>
      <Typography variant="caption" color="textSecondary">{label}{help ? ` — ${help}` : ''}</Typography>
      {list.map((f, i) => {
        const opMeta = FILTER_OPS.find((o) => o.op === f.op);
        return (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
            <Autocomplete
              freeSolo options={sourceHeaders} value={f.col || ''} style={{ width: 220 }} renderOption={renderOpt}
              onChange={(_, v) => set(i, { ...f, col: v || '' })}
              onInputChange={(_, v) => set(i, { ...f, col: v || '' })}
              renderInput={(p) => <TextField {...p} size="small" label="Source column" />}
            />
            <TextField select size="small" label="Condition" value={f.op} style={{ width: 220 }}
              onChange={(e) => set(i, { ...f, op: e.target.value })}>
              {FILTER_OPS.map((o) => <MenuItem key={o.op} value={o.op}>{o.label}</MenuItem>)}
            </TextField>
            {opMeta?.needsValue && (
              <TextField size="small" label="Value" value={f.value ?? ''} style={{ width: 140 }}
                onChange={(e) => set(i, { ...f, value: e.target.value })} />
            )}
            <IconButton size="small" aria-label="remove filter" onClick={() => remove(i)}><DeleteOutlineIcon fontSize="small" /></IconButton>
          </div>
        );
      })}
      <Button size="small" startIcon={<AddIcon />} onClick={add} style={{ marginTop: 4 }}>Add condition</Button>
    </div>
  );
}

export default function MappingBuilder() {
  const [specList, setSpecList] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [name, setName] = useState('');
  const [matchingType, setMatchingType] = useState('');
  const [active, setActive] = useState(true);

  const [spec, setSpec] = useState(() => normalizeSpec(STARTER_SPEC));
  const [specText, setSpecText] = useState('');       // JSON view buffer
  const [viewMode, setViewMode] = useState('visual'); // 'visual' | 'json'
  const [activeOutput, setActiveOutput] = useState(0);
  const [selectedKey, setSelectedKey] = useState('');

  const [targetColumns, setTargetColumns] = useState([]);
  const [sampleHeaders, setSampleHeaders] = useState([]);
  const [sampleRows, setSampleRows] = useState([]);
  const [sampleName, setSampleName] = useState('');

  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);
  const [sqlSources, setSqlSources] = useState([]);
  const [sqlSourceId, setSqlSourceId] = useState('');
  const [converting, setConverting] = useState(false);
  const fileRef = useRef(null);

  const targetOrder = useMemo(() => targetColumns.map((c) => c.key), [targetColumns]);

  async function refreshList() {
    const res = await listSpecs().catch(() => null);
    setSpecList(res?.data ?? []);
  }
  useEffect(() => {
    refreshList();
    listSqlSources().then((r) => setSqlSources(r?.data ?? [])).catch(() => {});
    listTargetColumns().then((r) => setTargetColumns(r?.data ?? [])).catch(() => {});
  }, []);

  // ---- spec mutation helpers (visual mode; keep immutability so React re-renders) ----
  const output = spec.outputs[activeOutput] || spec.outputs[0];
  const fields = useMemo(() => output?.fields || {}, [output]);

  function mutateOutput(idx, updater) {
    setSpec((s) => {
      const outputs = s.outputs.map((o, i) => (i === idx ? updater(o) : o));
      return { ...s, outputs };
    });
  }
  function setFieldRule(targetKey, rule) {
    mutateOutput(activeOutput, (o) => {
      const nextFields = { ...(o.fields || {}) };
      if (rule === undefined) delete nextFields[targetKey];
      else nextFields[targetKey] = rule;
      return { ...o, fields: nextFields };
    });
  }
  function setOutputWhen(when) {
    mutateOutput(activeOutput, (o) => ({ ...o, when }));
  }
  function addOutput() {
    setSpec((s) => {
      const clone = JSON.parse(JSON.stringify(s.outputs[activeOutput] || { fields: {} }));
      const labels = [...s.ui.outputLabels, `Output ${s.outputs.length + 1}`];
      return { ...s, outputs: [...s.outputs, clone], ui: { ...s.ui, outputLabels: labels } };
    });
    setActiveOutput(spec.outputs.length);
  }
  function removeOutput(idx) {
    setSpec((s) => {
      if (s.outputs.length <= 1) return s;
      return {
        ...s,
        outputs: s.outputs.filter((_, i) => i !== idx),
        ui: { ...s.ui, outputLabels: s.ui.outputLabels.filter((_, i) => i !== idx) },
      };
    });
    setActiveOutput((i) => Math.max(0, i >= idx ? i - 1 : i));
  }
  function renameOutput(idx, label) {
    setSpec((s) => ({ ...s, ui: { ...s.ui, outputLabels: s.ui.outputLabels.map((l, i) => (i === idx ? label : l)) } }));
  }

  // ---- view switching (keep visual spec and JSON text in sync) ----
  function switchView(next) {
    if (next === viewMode) return;
    setError('');
    if (next === 'json') {
      setSpecText(JSON.stringify(serializeSpec(spec, targetOrder), null, 2));
      setViewMode('json');
    } else {
      let parsed;
      try { parsed = JSON.parse(specText); } catch (e) { setError('Spec is not valid JSON: ' + e.message); return; }
      setSpec(normalizeSpec(parsed));
      setActiveOutput(0);
      setViewMode('visual');
    }
  }

  // Current spec object regardless of view (for preview + save).
  function currentSpec() {
    if (viewMode === 'json') {
      const parsed = JSON.parse(specText); // throws → caller handles
      return normalizeSpec(parsed);
    }
    return spec;
  }

  async function convertFromSql() {
    if (!sqlSourceId) return;
    setError(''); setMsg(''); setPreview(null); setConverting(true);
    const res = await convertSqlToSpec(sqlSourceId).then((r) => r.data).catch(() => null);
    setConverting(false);
    if (!res?.spec) { setError('Could not convert that SQL transform.'); return; }
    setSelectedId('');
    setName(res.spec.name || '');
    setMatchingType('');
    setActive(true);
    setSpec(normalizeSpec(res.spec));
    setActiveOutput(0);
    setViewMode('visual');
    const warns = res.warnings || [];
    setMsg(warns.length
      ? `Draft generated with ${warns.length} thing(s) to review: ${warns.slice(0, 5).join('; ')}${warns.length > 5 ? ' …' : ''}. Set the matching strategy, review, then Save.`
      : 'Draft generated cleanly. Set the matching strategy, review against a sample, then Save.');
  }

  async function loadSpec(id) {
    setSelectedId(id);
    setError(''); setMsg(''); setPreview(null); setSelectedKey(''); setActiveOutput(0); setViewMode('visual');
    if (!id) { setName(''); setMatchingType(''); setActive(true); setSpec(normalizeSpec(STARTER_SPEC)); return; }
    const res = await getSpec(id).catch(() => null);
    if (!res?.data) { setError('Failed to load spec.'); return; }
    const row = res.data;
    setName(row.name || '');
    setMatchingType(row.matching_type || '');
    setActive(row.active_ind !== 0);
    const parsed = typeof row.spec === 'string' ? JSON.parse(row.spec) : row.spec;
    setSpec(normalizeSpec(parsed));
  }

  function onSampleFile(file) {
    if (!file) return;
    setSampleName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      const wb = XLSX.read(new Uint8Array(e.target.result), { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const objs = XLSX.utils.sheet_to_json(ws, { defval: '', raw: false });
      const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
      setSampleHeaders((aoa[0] || Object.keys(objs[0] || {})).map(String));
      setSampleRows(objs.slice(0, 500));
    };
    reader.readAsArrayBuffer(file);
  }

  function runPreview() {
    setError(''); setMsg('');
    let s;
    try { s = currentSpec(); } catch (e) { setError('Spec is not valid JSON: ' + e.message); return; }
    if (!Array.isArray(s.outputs) || !s.outputs.length) { setError('Spec needs at least one output row.'); return; }
    if (!sampleRows.length) { setError('Upload a sample file first.'); return; }
    try {
      const out = applyMapping(s, sampleRows);
      const cols = specColumns(serializeSpec(s, targetOrder));
      setPreview({ cols, rows: out.slice(0, 100), total: out.length, sampleIn: sampleRows.length });
    } catch (e) { setError('Preview failed: ' + e.message); }
  }

  const missingCols = useMemo(() => {
    if (!sampleHeaders.length) return [];
    let s; try { s = viewMode === 'json' ? JSON.parse(specText) : spec; } catch { return []; }
    const hs = new Set(sampleHeaders);
    return referencedColumns(s).filter((c) => !hs.has(c));
  }, [spec, specText, viewMode, sampleHeaders]);

  async function save() {
    setError(''); setMsg('');
    let s;
    try { s = currentSpec(); } catch (e) { setError('Spec is not valid JSON: ' + e.message); return; }
    if (!name.trim()) { setError('Name is required.'); return; }
    const payloadSpec = serializeSpec({ ...s, name: name.trim() }, targetOrder);
    if (!payloadSpec.outputs.length) { setError('Spec needs at least one output row.'); return; }
    setSaving(true);
    const res = await saveSpec({ id: selectedId || undefined, name: name.trim(), spec: payloadSpec, matchingType, active })
      .catch((err) => err?.response ?? null);
    setSaving(false);
    if (!res || res.status >= 400) { setError(res?.data?.message || 'Save failed.'); return; }
    setMsg(`Saved "${name.trim()}".`);
    if (!selectedId && res.data?.id) setSelectedId(String(res.data.id));
    refreshList();
  }

  // Target columns grouped for the right-hand list, groups in first-seen order.
  const groupedTargets = useMemo(() => {
    const groups = [];
    const byName = {};
    targetColumns.forEach((c) => {
      if (!byName[c.group]) { byName[c.group] = { group: c.group, items: [] }; groups.push(byName[c.group]); }
      byName[c.group].items.push(c);
    });
    return groups;
  }, [targetColumns]);

  // First sample row (header → value) so source dropdowns can preview real data next to each column.
  const sampleFirstRow = useMemo(() => sampleRows[0] || {}, [sampleRows]);
  // Source columns already consumed by a mapping in the active output — flagged (struck) in the pickers.
  const usedSources = useMemo(() => {
    const s = new Set();
    Object.values(fields).forEach((r) => collectSourceCols(r, s));
    return s;
  }, [fields]);

  const mappedCount = Object.keys(fields).length;

  return (
    <div style={{ padding: '30px 20px' }}>
      <Paper style={{ padding: '20px' }}>
        <Typography variant="h4" gutterBottom>Mapping Rule Builder</Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Map a vendor/ERP file (RFMS, QFloors, …) onto the showroom-pricing columns. Upload a sample, connect each
          source column to its target with optional transforms, then save. Use multiple <b>output rows</b> when one
          source line must become two priced rows (e.g. a cut cost and a roll cost).
        </Typography>

        <Grid container spacing={2} style={{ marginTop: 8 }}>
          <Grid item xs={12} md={3}>
            <TextField select fullWidth label="Spec" value={selectedId} onChange={(e) => loadSpec(e.target.value)}>
              <MenuItem value="">— New spec —</MenuItem>
              {specList.map((s) => (
                <MenuItem key={s.id} value={String(s.id)}>{s.name}{s.active_ind === 0 ? ' (inactive)' : ''}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField fullWidth label="Vendor / transform name" value={name} onChange={(e) => setName(e.target.value)} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField select fullWidth label="Matching strategy" value={matchingType} onChange={(e) => setMatchingType(e.target.value)}>
              {MATCHING_TYPES.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControlLabel control={<Checkbox checked={active} onChange={(e) => setActive(e.target.checked)} />} label="Active" />
          </Grid>
        </Grid>

        {sqlSources.length ? (
          <Grid container spacing={2} alignItems="center" style={{ marginTop: 4 }}>
            <Grid item xs={12} md={5}>
              <TextField select fullWidth size="small" label="Start from a legacy SQL/JSON transform" value={sqlSourceId} onChange={(e) => setSqlSourceId(e.target.value)}>
                <MenuItem value="">—</MenuItem>
                {sqlSources.map((s) => <MenuItem key={s.id} value={String(s.id)}>{s.name}{s.transform_type ? ` (${s.transform_type})` : ''}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button variant="outlined" onClick={convertFromSql} disabled={!sqlSourceId || converting}>
                {converting ? <CircularProgress size={18} /> : 'Convert to draft'}
              </Button>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="caption" color="textSecondary">
                Auto-generates a draft from the old SQL — review it, set the matching strategy, preview against a sample, then Save.
              </Typography>
            </Grid>
          </Grid>
        ) : null}

        <Divider style={{ margin: '16px 0' }} />

        {/* Sample upload */}
        <div>
          <Button variant="outlined" onClick={() => fileRef.current?.click()}>Upload sample file</Button>
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" style={{ display: 'none' }}
            onChange={(e) => onSampleFile(e.target.files?.[0])} />
          {sampleName ? <Typography variant="caption" style={{ marginLeft: 10 }}>{sampleName} — {sampleRows.length} sample rows</Typography> : null}
          {missingCols.length ? (
            <Typography variant="caption" style={{ color: '#b26a00', display: 'block', marginTop: 8 }}>
              Spec references columns not in the sample: {missingCols.join(', ')}
            </Typography>
          ) : null}
        </div>

        {/* View tabs */}
        <Tabs value={viewMode} onChange={(_, v) => switchView(v)} style={{ marginTop: 12 }} indicatorColor="primary" textColor="primary">
          <Tab value="visual" label="Visual" />
          <Tab value="json" label="Advanced (JSON)" />
        </Tabs>
        <Divider />

        {viewMode === 'json' ? (
          <div style={{ marginTop: 12 }}>
            <TextField
              label="Spec (JSON)" multiline minRows={18} fullWidth variant="outlined"
              value={specText} onChange={(e) => setSpecText(e.target.value)}
              InputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
            />
          </div>
        ) : (
          <div style={{ marginTop: 12 }}>
            {/* Output-row tabs */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <Tabs value={activeOutput} onChange={(_, v) => { setActiveOutput(v); setSelectedKey(''); }}
                variant="scrollable" scrollButtons="auto" style={{ minHeight: 40 }}>
                {spec.outputs.map((o, i) => (
                  <Tab key={i} style={{ minHeight: 40 }} label={spec.ui.outputLabels[i] || `Output ${i + 1}`} />
                ))}
              </Tabs>
              <Button size="small" startIcon={<AddIcon />} onClick={addOutput}>Add output row</Button>
            </div>
            <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 4 }}>
              Each output row that passes its conditions produces one priced row per source line. Use two rows to split a
              file's cut cost and roll cost into separate SKUs.
            </Typography>

            {/* Active output header controls */}
            <Grid container spacing={2} alignItems="center" style={{ marginTop: 4 }}>
              <Grid item xs={12} md={4}>
                <TextField size="small" fullWidth label="Output row name"
                  value={spec.ui.outputLabels[activeOutput] || ''}
                  onChange={(e) => renameOutput(activeOutput, e.target.value)} />
              </Grid>
              <Grid item xs={12} md={8}>
                {spec.outputs.length > 1 && (
                  <Button size="small" color="secondary" startIcon={<DeleteOutlineIcon />} onClick={() => removeOutput(activeOutput)}>
                    Remove this output row
                  </Button>
                )}
              </Grid>
            </Grid>

            <FilterBuilder
              label="Only emit this output row when"
              help="all conditions pass (leave empty to always emit)"
              filters={output?.when} sourceHeaders={sampleHeaders}
              sampleFirstRow={sampleFirstRow} usedSources={usedSources} onChange={setOutputWhen}
            />

            <Divider style={{ margin: '14px 0' }} />

            {/* 2-column mapping area: showroom-pricing columns (left) → rule editor (right) */}
            <Grid container spacing={2}>
              {/* Left — all showroom-pricing target columns */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">
                  Showroom-pricing columns <span style={{ color: '#888', fontWeight: 400 }}>({mappedCount} mapped)</span>
                </Typography>
                <div style={{ maxHeight: 520, overflow: 'auto', marginTop: 6 }}>
                  {groupedTargets.map((g) => (
                    <div key={g.group} style={{ marginBottom: 8 }}>
                      <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>{g.group}</Typography>
                      {g.items.map((c) => {
                        const rule = fields[c.key];
                        const kind = detectRuleKind(rule);
                        const isMapped = kind !== RULE_KINDS.UNMAPPED;
                        const isSel = selectedKey === c.key;
                        return (
                          <div key={c.key} onClick={() => setSelectedKey(c.key)}
                            style={{
                              display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
                              padding: '5px 8px', margin: '3px 0', cursor: 'pointer', borderRadius: 4,
                              border: isSel ? '1px solid #1976d2' : '1px solid #eee',
                              borderLeft: isMapped ? '4px solid #43a047' : '4px solid #ddd',
                              background: isSel ? '#e3f2fd' : '#fff',
                            }}>
                            <span style={{ fontSize: 13, color: isMapped ? '#222' : '#999' }}>{c.label}</span>
                            <span style={{ fontSize: 11, color: kind === RULE_KINDS.ADVANCED ? '#b26a00' : '#666', fontFamily: 'monospace', textAlign: 'right', maxWidth: '55%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {isMapped ? summarizeRule(rule) : ''}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </Grid>

              {/* Right — transform / rule editor for the selected slot */}
              <Grid item xs={12} md={6}>
                <div style={{ border: '1px solid #eee', borderRadius: 4, padding: 12, minHeight: 220, background: '#fafafa' }}>
                  <SlotEditor
                    targetKey={selectedKey}
                    targetLabel={targetColumns.find((c) => c.key === selectedKey)?.label}
                    rule={selectedKey ? fields[selectedKey] : undefined}
                    sourceHeaders={sampleHeaders}
                    sampleFirstRow={sampleFirstRow}
                    usedSources={usedSources}
                    onChange={(rule) => setFieldRule(selectedKey, rule)}
                  />
                </div>
              </Grid>
            </Grid>

            <Divider style={{ margin: '14px 0' }} />

            {/* Spec-level options */}
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FilterBuilder
                  label="Skip source rows unless"
                  help="whole-file gate applied before any output row"
                  filters={spec.rowFilters} sourceHeaders={sampleHeaders}
                  sampleFirstRow={sampleFirstRow} usedSources={usedSources}
                  onChange={(rowFilters) => setSpec((s) => ({ ...s, rowFilters }))}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="textSecondary">Fill down (carry a blank cell's previous value — for merged/grouped source rows)</Typography>
                <Autocomplete
                  multiple size="small" options={sampleHeaders} value={spec.fillDown || []}
                  onChange={(_, v) => setSpec((s) => ({ ...s, fillDown: v }))}
                  renderInput={(p) => <TextField {...p} variant="outlined" size="small" placeholder="Source columns" />}
                  style={{ marginTop: 6 }}
                />
              </Grid>
            </Grid>
          </div>
        )}

        <div style={{ marginTop: 16 }}>
          <Button variant="contained" onClick={runPreview} style={{ marginRight: 10 }}>Preview against sample</Button>
          <Button variant="contained" color="primary" onClick={save} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : 'Save spec'}
          </Button>
        </div>

        {error ? <Typography style={{ color: '#f44336', marginTop: 12 }}>{error}</Typography> : null}
        {msg ? <Typography style={{ color: '#388E3C', marginTop: 12 }}>{msg}</Typography> : null}

        {preview ? (
          <div style={{ marginTop: 16 }}>
            <Typography variant="body2" color="textSecondary">
              {preview.total} output row{preview.total === 1 ? '' : 's'} from {preview.sampleIn} sample rows
              {' '}({preview.cols.length} columns). Showing first {preview.rows.length}.
            </Typography>
            <div style={{ overflow: 'auto', maxHeight: 380, border: '1px solid #eee', marginTop: 8 }}>
              <Table size="small" stickyHeader style={{ width: 'auto', tableLayout: 'auto' }}>
                <TableHead>
                  <TableRow>{preview.cols.map((c) => <TableCell key={c} style={{ fontWeight: 'bold', whiteSpace: 'nowrap', padding: '4px 12px', background: '#fafafa' }}>{c}</TableCell>)}</TableRow>
                </TableHead>
                <TableBody>
                  {preview.rows.map((r, i) => (
                    <TableRow key={i}>{preview.cols.map((c) => <TableCell key={c} style={{ whiteSpace: 'nowrap', padding: '4px 12px' }}>{r[c]}</TableCell>)}</TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        ) : null}
      </Paper>
    </div>
  );
}
